import { useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Snackbar,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { collection, onSnapshot, orderBy, query } from "firebase/firestore";
import { db } from "../lib/firebase";
import { api } from "../lib/api";
import { useSession } from "../auth/AuthProvider";
import { formatJalali } from "../lib/format";

interface Lead {
  id: string;
  name: string;
  phone: string;
  source: string;
  eventDateApprox: string | null;
  guestEstimate: number | null;
  note: string | null;
  stage: string;
}

const STAGES: { value: string; label: string; color: "default" | "info" | "warning" | "success" | "error" }[] = [
  { value: "new", label: "جدید", color: "default" },
  { value: "contacted", label: "تماس گرفته", color: "info" },
  { value: "visited", label: "بازدید کرده", color: "info" },
  { value: "quoted", label: "قیمت داده", color: "warning" },
  { value: "won", label: "موفق 🎉", color: "success" },
  { value: "lost", label: "ناموفق", color: "error" },
];

const SOURCE_FA: Record<string, string> = {
  walk_in: "حضوری",
  phone: "تلفنی",
  instagram: "انستاگرام",
  referral: "معرفی",
  other: "دیگر",
};

const NEXT_STAGE: Record<string, string> = {
  new: "contacted",
  contacted: "visited",
  visited: "quoted",
  quoted: "won",
};

export function CrmPage() {
  const { orgId } = useSession();
  const [leads, setLeads] = useState<Lead[]>([]);
  const [addOpen, setAddOpen] = useState(false);
  const [form, setForm] = useState({ name: "", phone: "", source: "phone", guests: "", note: "" });
  const [toast, setToast] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!orgId) return;
    const q = query(
      collection(db, "organizations", orgId, "leads"),
      orderBy("createdAt", "desc"),
    );
    return onSnapshot(q, (snap) => {
      setLeads(snap.docs.map((d) => ({ id: d.id, ...(d.data() as Omit<Lead, "id">) })));
    });
  }, [orgId]);

  async function addLead() {
    setBusy(true);
    try {
      await api(`/orgs/${orgId}/crm/leads`, {
        method: "POST",
        body: {
          name: form.name,
          phone: form.phone,
          source: form.source,
          guestEstimate: form.guests ? Number(form.guests) : undefined,
          note: form.note || undefined,
        },
      });
      setToast("سرنخ ثبت شد");
      setAddOpen(false);
      setForm({ name: "", phone: "", source: "phone", guests: "", note: "" });
    } catch (e) {
      setToast(e instanceof Error ? e.message : "خطا");
    } finally {
      setBusy(false);
    }
  }

  async function setStage(lead: Lead, stage: string) {
    try {
      await api(`/orgs/${orgId}/crm/leads/${lead.id}`, {
        method: "PATCH",
        body: { stage },
      });
    } catch (e) {
      setToast(e instanceof Error ? e.message : "خطا");
    }
  }

  return (
    <>
      <Stack direction="row" alignItems="center" mb={2} spacing={2}>
        <Typography variant="h5" sx={{ flex: 1 }}>
          مشتریان بالقوه (سرنخ‌ها)
        </Typography>
        <Button variant="contained" onClick={() => setAddOpen(true)}>
          + سرنخ جدید
        </Button>
      </Stack>

      <Box sx={{ display: "flex", gap: 2, overflowX: "auto", pb: 2 }}>
        {STAGES.map((stage) => {
          const items = leads.filter((l) => l.stage === stage.value);
          return (
            <Box key={stage.value} sx={{ minWidth: 230, flex: "0 0 230px" }}>
              <Stack direction="row" alignItems="center" spacing={1} mb={1}>
                <Chip size="small" color={stage.color} label={stage.label} />
                <Typography variant="caption" color="text.secondary">
                  {items.length}
                </Typography>
              </Stack>
              <Stack spacing={1}>
                {items.map((lead) => (
                  <Card key={lead.id} variant="outlined">
                    <CardContent sx={{ p: 1.5, "&:last-child": { pb: 1.5 } }}>
                      <Typography variant="subtitle2">{lead.name}</Typography>
                      <Typography variant="body2" color="text.secondary" dir="ltr" textAlign="right">
                        {lead.phone}
                      </Typography>
                      <Stack direction="row" spacing={0.5} mt={0.5} flexWrap="wrap">
                        <Chip size="small" variant="outlined" label={SOURCE_FA[lead.source] ?? lead.source} />
                        {lead.guestEstimate && (
                          <Chip size="small" variant="outlined" label={`~${lead.guestEstimate} مهمان`} />
                        )}
                      </Stack>
                      {lead.eventDateApprox && (
                        <Typography variant="caption" color="text.secondary">
                          حدوداً {formatJalali(lead.eventDateApprox)}
                        </Typography>
                      )}
                      {lead.note && (
                        <Typography variant="caption" display="block" color="text.secondary">
                          {lead.note}
                        </Typography>
                      )}
                      {!["won", "lost"].includes(lead.stage) && (
                        <Stack direction="row" spacing={1} mt={1}>
                          <Button
                            size="small"
                            variant="contained"
                            onClick={() => setStage(lead, NEXT_STAGE[lead.stage])}
                          >
                            {STAGES.find((s) => s.value === NEXT_STAGE[lead.stage])?.label} ←
                          </Button>
                          <Button size="small" color="error" onClick={() => setStage(lead, "lost")}>
                            ناموفق
                          </Button>
                        </Stack>
                      )}
                    </CardContent>
                  </Card>
                ))}
              </Stack>
            </Box>
          );
        })}
      </Box>

      <Dialog open={addOpen} onClose={() => setAddOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>سرنخ جدید</DialogTitle>
        <DialogContent>
          <Stack spacing={2} mt={1}>
            <TextField
              label="نام"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              autoFocus
            />
            <TextField
              label="شماره تماس"
              placeholder="07XXXXXXXXX"
              value={form.phone}
              onChange={(e) => setForm({ ...form, phone: e.target.value })}
            />
            <TextField
              select
              label="منبع"
              value={form.source}
              onChange={(e) => setForm({ ...form, source: e.target.value })}
            >
              {Object.entries(SOURCE_FA).map(([value, label]) => (
                <MenuItem key={value} value={value}>
                  {label}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              label="تخمین مهمانان (اختیاری)"
              type="number"
              value={form.guests}
              onChange={(e) => setForm({ ...form, guests: e.target.value })}
            />
            <TextField
              label="یادداشت (اختیاری)"
              value={form.note}
              onChange={(e) => setForm({ ...form, note: e.target.value })}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddOpen(false)}>انصراف</Button>
          <Button
            variant="contained"
            onClick={addLead}
            disabled={busy || form.name.trim().length < 2 || !/^(\+93|0)\d{9}$/.test(form.phone)}
          >
            ثبت
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={!!toast} autoHideDuration={4000} onClose={() => setToast(null)}>
        <Alert onClose={() => setToast(null)} severity="info">
          {toast}
        </Alert>
      </Snackbar>
    </>
  );
}
